import { Button, Drawer, FormField, Input, SegmentedControl } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectAdvancedValues, ProjectFilterState } from '../hooks/useProjectFilters';
import { PROJECT_UNITS } from '../types/ProjectList';

const TITLE_ID = 'project-advanced-filters-title';
const FORM_ID = 'project-advanced-filters';

type YesNoChoice = 'all' | 'yes' | 'no';

const toChoice = (value: boolean | null): YesNoChoice =>
  value === null ? 'all' : value ? 'yes' : 'no';
const fromChoice = (choice: YesNoChoice): boolean | null =>
  choice === 'all' ? null : choice === 'yes';

type ProjectFiltersDrawerProps = {
  open: boolean;
  onClose: () => void;
  filterState: ProjectFilterState;
};

function draftFrom({ filters }: ProjectFilterState): ProjectAdvancedValues {
  return {
    unitId: filters.unitId ?? null,
    axisCode: filters.axisCode ?? '',
    name: filters.name ?? '',
    contractSearch: filters.contractSearch ?? '',
    internal: filters.internal ?? null,
    pool: filters.pool ?? null,
  };
}

/** No molde de `PoolFiltersDrawer`: rascunho aplicado de uma vez; fechar sem aplicar o descarta. */
export function ProjectFiltersDrawer({ open, onClose, filterState }: ProjectFiltersDrawerProps) {
  const { t } = useTranslation(['projetos', 'alocacao', 'common']);
  const [draft, setDraft] = useState(() => draftFrom(filterState));
  const [wasOpen, setWasOpen] = useState(open);

  // Reinicia o rascunho a partir do que está aplicado sempre que o painel abre.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(draftFrom(filterState));
  }

  const apply = () => {
    filterState.applyAdvanced(draft);
    onClose();
  };

  const yesNoField = (key: 'internal' | 'pool', label: string) => (
    <FormField label={label}>
      <SegmentedControl
        aria-label={label}
        value={toChoice(draft[key])}
        onValueChange={(choice) =>
          setDraft((d) => ({ ...d, [key]: fromChoice(choice as YesNoChoice) }))
        }
      >
        <SegmentedControl.Item value="all">{t('projetos:filtros.todos')}</SegmentedControl.Item>
        <SegmentedControl.Item value="yes">{t('common:yes')}</SegmentedControl.Item>
        <SegmentedControl.Item value="no">{t('common:no')}</SegmentedControl.Item>
      </SegmentedControl>
    </FormField>
  );

  return (
    <Drawer open={open} onClose={onClose} size="md" ariaLabelledBy={TITLE_ID}>
      <Drawer.Header
        titleId={TITLE_ID}
        dismissLabel={t('common:close')}
        title={t('alocacao:filtros.avancados')}
        subtitle={t('projetos:filtros.avancadosDescricao')}
        onClose={onClose}
      />
      <Drawer.Body>
        <form
          id={FORM_ID}
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <FormField label={t('projetos:colunas.unidade')}>
            <SegmentedControl
              aria-label={t('projetos:colunas.unidade')}
              value={draft.unitId === null ? 'all' : String(draft.unitId)}
              onValueChange={(choice) =>
                setDraft((d) => ({ ...d, unitId: choice === 'all' ? null : Number(choice) }))
              }
            >
              <SegmentedControl.Item value="all">
                {t('projetos:filtros.todos')}
              </SegmentedControl.Item>
              {PROJECT_UNITS.map((unit) => (
                <SegmentedControl.Item key={unit.id} value={String(unit.id)}>
                  {unit.code}
                </SegmentedControl.Item>
              ))}
            </SegmentedControl>
          </FormField>
          <FormField label={t('projetos:colunas.codigo')}>
            <Input
              value={draft.axisCode}
              onChange={(e) => setDraft((d) => ({ ...d, axisCode: e.target.value }))}
            />
          </FormField>
          <FormField label={t('projetos:colunas.nome')}>
            <Input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </FormField>
          <FormField label={t('projetos:colunas.contrato')}>
            <Input
              value={draft.contractSearch}
              onChange={(e) => setDraft((d) => ({ ...d, contractSearch: e.target.value }))}
            />
          </FormField>
          {yesNoField('internal', t('projetos:colunas.interno'))}
          {yesNoField('pool', t('projetos:colunas.pool'))}
        </form>
      </Drawer.Body>
      <Drawer.Footer className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t('alocacao:filtros.cancelar')}
        </Button>
        <Button type="submit" form={FORM_ID}>
          {t('alocacao:filtros.aplicar')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}
